import { Component, computed, DestroyRef, effect, inject, input, Signal, signal, WritableSignal } from '@angular/core'
import {
  AbstractControl,
  FormArray,
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  ValidationErrors,
  ValidatorFn,
  Validators,
} from '@angular/forms'
import {
  BricksAlertComponent,
  BricksDatePickerComponent,
  BricksErrorComponent,
  BricksIconComponent,
  BricksInputComponent,
  BricksLabelComponent,
} from '@enbw_bricks/ui'
import { GeneralFormControls, OfferFormService } from '../../shared/services/offer-form.service'
import { ProductionTechnology } from '../../shared/models/production-technology.enum'
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop'
import { MatExpansionModule } from '@angular/material/expansion'
import { DigitsOnlyDirective } from '../../shared/directives/digits-only.directive'
import { SolarSpecificTechnicalDataComponent } from './solar-specific-technical-data/solar-specific-technical-data.component'
import { WindSpecificTechnicalDataComponent } from './wind-specific-technical-data/wind-specific-technical-data.component'
import { TooltipComponent } from '../../shared/components/tooltip/tooltip.component'
import moment from 'moment'
import { MatDialog } from '@angular/material/dialog'
import { MultiResourcePopupComponent } from './multi-resource-popup/multi-resource-popup.component'
import { ResourceDeletionConfirmationComponent } from './resource-deletion-confirmation/resource-deletion-confirmation.component'
import {
  GenericResourceFormControls,
  MarketRegisterResource,
  MarketRegisterSolarResource,
  MarketRegisterWindResource,
  SolarResourceFormControls,
  WindResourceFormControls,
} from '../../shared/models/technical-resource'
import { NotificationService } from '../../shared/components/notification/notification.service'

import { MarketRegisterDataService } from '../../shared/services/market-register-data.service'
import { HttpStatusCode } from '@angular/common/http'
import { MarketingType } from '../../shared/models/marketing-type.enum'
import {
  DIGIT_WITH_UP_TO_THREE_DECIMALS,
  DIGIT_WITH_UP_TO_TWO_DECIMALS,
  GENERATION_UNIT_PATTERN,
} from '../../shared/validators/validator-pattern'
import { CustomValidators } from '../../shared/validators/custom-validators'
import { FormatViewDirective } from '../../shared/directives/format-view.directive'
import { InputGeoComponent } from '../../shared/components/inputs/input-geo/input-geo.component'
import { ViewFormatterService } from '../../shared/services/view-formatter.service'
import { MapComponent } from './map/map.component'
import { LATITUDE_RANGE, LONGITUDE_RANGE } from './map/map-config'
import { AbstractTrackEventsService } from '../../shared/services/abstract-track-events.service'

@Component({
  selector: 'app-technical-resource-data',
  imports: [
    BricksInputComponent,
    BricksErrorComponent,
    BricksIconComponent,
    BricksDatePickerComponent,
    BricksLabelComponent,
    BricksIconComponent,
    MatExpansionModule,
    ReactiveFormsModule,
    TooltipComponent,
    DigitsOnlyDirective,
    SolarSpecificTechnicalDataComponent,
    WindSpecificTechnicalDataComponent,
    FormatViewDirective,
    InputGeoComponent,
    MapComponent,
    BricksAlertComponent,
  ],
  templateUrl: './technical-resource-data.component.html',
  styleUrl: './technical-resource-data.component.scss',
})
export class TechnicalResourceDataComponent {
  private readonly COMMISSIONING_DATE_MIN = new Date(1990, 0, 1)
  readonly isFormSubmitted = input<boolean>(false)
  private readonly trackEventService = inject(AbstractTrackEventsService)
  private readonly offerFormService = inject(OfferFormService)
  private readonly marketDataService = inject(MarketRegisterDataService)
  private readonly notificationService = inject(NotificationService)
  private readonly dialog = inject(MatDialog)
  private readonly destroyRef = inject(DestroyRef)

  getInstalledCapacityError(control: FormControl): string {
    if (control.hasError('zeroIsNotAllowed')) {
      return CustomValidators.zeroIsNotAllowedErrorMessage
    } else if (control.hasError('pattern')) {
      return CustomValidators.threeDecimalsErrorMessage
    } else if (control.hasError('min') || control.hasError('max')) {
      return 'Der Wert muss zwischen 1 und 999.999 liegen.'
    }
    return ''
  }

  getCommissioningDateError(resource: FormGroup<SolarResourceFormControls | WindResourceFormControls>): string {
    if (this.offerFormService.isMarketRegisterCommissioningDateInvalid(resource)) {
      return 'Entspricht nicht dem Betriebsstatus Ihrer Anlage'
    } else if (resource.controls.commissioningDateCommon.hasError('dateInPastNotAllowed')) {
      return 'Das Datum darf nicht mehr als 6 Monate in der Zukunft liegen.'
    } else if (resource.controls.commissioningDateCommon.hasError('dateInFutureNotAllowed')) {
      return 'Entspricht nicht dem Betriebsstatus Ihrer Anlage.'
    } else if (resource.controls.commissioningDateCommon.hasError('dateBeforeMinDateNotAllowed')) {
      return `Das Datum darf nicht vor dem ${this.COMMISSIONING_DATE_MIN.toLocaleDateString()} liegen.`
    } else if (resource.controls.commissioningDateCommon.hasError('dateLaterThanSixMonthNotAllowed')) {
      return 'Datum darf nicht mehr als 6 Monate in der Zukunft liegen.'
    }
    return ''
  }

  getApplicableValueCentsPerkWhError(control: FormControl): string {
    if (control.hasError('zeroIsNotAllowed')) {
      return CustomValidators.zeroIsNotAllowedErrorMessage
    } else if (control.hasError('pattern')) {
      return 'Es sind maximal 2 Nachkommastellen erlaubt.'
    } else if (control.hasError('min') || control.hasError('max')) {
      return 'Der Wert muss zwischen 1 und 99,99 liegen.'
    }
    return ''
  }

  private readonly locationValidMap = new Map<number, WritableSignal<boolean>>()
  private readonly formGroup: FormGroup<GeneralFormControls> = this.offerFormService.generalData
  protected readonly formArray: FormArray<FormGroup<WindResourceFormControls | SolarResourceFormControls>> =
    this.offerFormService.generalData.controls.technicalResources

  protected readonly ProductionTechnology = ProductionTechnology
  protected readonly MAX_RESOURCES_COUNT = 50

  protected readonly resourceCounter: Signal<number> = computed(() => this.allResources()?.length || 0)
  protected readonly deleteResourceButtonVisible: Signal<boolean> = computed(() => this.resourceCounter() > 1)
  protected readonly productionTechnology: Signal<ProductionTechnology | null> = toSignal(
    this.formGroup.controls.productionTechnology.valueChanges,
    {
      initialValue: this.formGroup.controls.productionTechnology.value,
    }
  )

  protected readonly resourceCreationBlocked: Signal<boolean> = computed(
    () => this.productionTechnology() == null || this.isAlreadyInOperation() == null || !this.contractStatusDefined()
  )

  protected readonly addResourceButtonVisible: Signal<boolean> = computed(
    () => !this.resourceCreationBlocked() && this.resourceCounter() < this.MAX_RESOURCES_COUNT
  )

  protected readonly isAlreadyInOperation: Signal<boolean | null> = toSignal(
    this.formGroup.controls.isAlreadyInOperation.valueChanges,
    {
      initialValue: this.formGroup.controls.isAlreadyInOperation.value,
    }
  )

  private readonly allResources = toSignal(this.formArray.valueChanges, {
    initialValue: this.formArray.value,
  })

  readonly marketingType: Signal<MarketingType | null> = toSignal(this.formGroup.controls.marketingType.valueChanges, {
    initialValue: this.formGroup.controls.marketingType.value,
  })

  private readonly contractStartDate: Signal<moment.Moment | null> = toSignal(
    this.formGroup.controls.contractStartDate.valueChanges,
    { initialValue: this.formGroup.controls.contractStartDate.value }
  )

  private readonly contractEndDate: Signal<moment.Moment | null> = toSignal(
    this.formGroup.controls.contractEndDate.valueChanges,
    { initialValue: this.formGroup.controls.contractEndDate.value }
  )

  private readonly contractStatusDefined: Signal<boolean> = computed(
    () => this.contractStartDate() != null && this.contractEndDate() != null && this.marketingType() != null
  )

  private isResourceCreationAllowed(): boolean {
    return (
      this.formGroup.controls.productionTechnology.value != null &&
      this.formGroup.controls.isAlreadyInOperation.value != null &&
      this.formGroup.controls.contractStartDate.value != null &&
      this.formGroup.controls.contractEndDate.value != null &&
      this.formGroup.controls.marketingType.value != null
    )
  }

  private inPastFilter =
    (marketingType: MarketingType | null) =>
    (d: Date | null): boolean => {
      const date = d as unknown as moment.Moment
      if (!date) return true
      if (marketingType == MarketingType.Other) {
        return !date.isBefore(moment(this.COMMISSIONING_DATE_MIN)) && date.isBefore(moment())
      }
      return date.isBefore(moment())
    }

  fromNowTo184Filter = (d: Date | moment.Moment | null): boolean => {
    const date = d as unknown as moment.Moment
    if (!date) return true
    return date.isBetween(moment(), moment().add(184, 'days'))
  }

  readonly commissioningDateFilter = computed(() =>
    this.isAlreadyInOperation() ? this.inPastFilter(this.marketingType()) : this.fromNowTo184Filter
  )

  protected readonly commissioningDateInPastTooltip =
    'Bitte geben Sie das erstmalige Inbetriebnahmedatum nach der Definition des Marktstammdatenregisters an. Das Datum muss in der Vergangenheit liegen. Im Marktstammdatenregister findet sich die Angabe an folgender Stelle: Öffentliche Daten → Aktuelle Einheitenübersicht → Reiter "Stromerzeugungseinheiten" → Suche nach der "MaStR-Nr. der Einheit" → Reiter "EEG- Anlage" → Feld "Datum der erstmaligen Inbetriebnahme der EEG- Anlage".'

  protected readonly commissioningDateInFutureTooltip =
    'Bitte geben Sie an, wann Ihre Anlage voraussichtlich erstmalig in Betrieb genommen wird. Das Datum muss in der Zukunft liegen.'

  protected readonly federalNetworkAgencyLink =
    'https://www.bundesnetzagentur.de/DE/Fachthemen/ElektrizitaetundGas/ErneuerbareEnergien/EEG_Foerderung/start.html'

  protected readonly eegCalcLink = 'https://www.interconnector.de/eeg-rechner/'

  protected isEegCalcLinkVisible($index: number): boolean {
    const commissioningDateCommon = this.formArray.at($index)?.controls.commissioningDateCommon.value
    return !!(
      this.productionTechnology() === ProductionTechnology.Solar &&
      this.isAlreadyInOperation() &&
      moment.utc(commissioningDateCommon)?.isAfter(moment('2016-01-01').startOf('day'))
    )
  }

  protected readonly latitudeRange = LATITUDE_RANGE
  protected readonly longitudeRange = LONGITUDE_RANGE

  constructor() {
    effect(() => {
      // signal must be read before any other conditions in order to be tracked
      const inOperation = this.isAlreadyInOperation()
      if (!this.isResourceCreationAllowed()) {
        return
      }
      if (!this.formArray.length) {
        this.addResourceFormGroup()
      }
      this.formArray.controls.forEach((resource) => {
        ;(resource.get('commissioningDateCommon') as FormControl).updateValueAndValidity()
        this.setRequiredValidatorForApplicableValueCentsPerkWh(resource, inOperation ?? false)
      })
    })

    effect(() => {
      if (this.isResourceCreationAllowed()) {
        this.addResourceFormGroup()
      }
    })

    effect(() => {
      if (this.contractStatusDefined() && this.isResourceCreationAllowed() && !this.formArray.length) {
        this.addResourceFormGroup()
      }
    })
  }

  addResource() {
    if (this.formArray.length >= this.MAX_RESOURCES_COUNT) {
      return
    }
    const showMultiResourceHint = this.formArray.length === 1
    this.addResourceFormGroup()
    const newIndex = this.formArray.length - 1
    if (showMultiResourceHint) {
      this.dialog
        .open(MultiResourcePopupComponent, {
          data: this.productionTechnology(),
          width: '640px',
        })
        .afterClosed()
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe(() => this.scrollResourceSectionIntoView(newIndex))
    } else {
      this.scrollResourceSectionIntoView(newIndex)
    }
  }

  private scrollResourceSectionIntoView(index: number) {
    requestAnimationFrame(() => {
      document.getElementById(`technical-resource-${index}`)?.scrollIntoView({
        behavior: 'auto',
        block: 'start',
      })
    })
  }

  trackEventAddResource() {
    this.trackEventService.trackEventUnitAdded({
      event: 'unit_added',
      eventCategory: 'Button',
      eventAction: 'click',
      eventLabel: 'Add Generation Unit',
      unit_count: this.resourceCounter() + 1,
    })
  }

  private addResourceFormGroup() {
    const resource: FormGroup<WindResourceFormControls | SolarResourceFormControls> =
      this.productionTechnology() == ProductionTechnology.Solar ? this.buildSolarResource() : this.buildWindResource()
    this.formArray.push(resource)
    if (this.isFormSubmitted()) {
      resource.markAllAsTouched()
    }
  }

  private buildCommonResource(): FormGroup {
    const applicableValueValidators = this.validatorForApplicableValueCentsPerkWh()

    if (this.isAlreadyInOperation()) {
      applicableValueValidators.push(Validators.required)
    }
    const commonResource = new FormGroup({
      mastrIdGenerationUnit: new FormControl<string | null>('', {
        validators: [Validators.pattern(GENERATION_UNIT_PATTERN)],
        updateOn: 'blur',
      }),
      installedCapacityKW: new FormControl<number | null>(null, {
        validators: [
          Validators.required,
          Validators.min(1),
          Validators.max(999_999),
          Validators.pattern(DIGIT_WITH_UP_TO_THREE_DECIMALS),
          CustomValidators.zeroIsNotAllowedAsFirstCharacter(),
        ],
        updateOn: 'blur',
      }),
      commissioningDateCommon: new FormControl<moment.Moment | null>(null, {
        validators: [
          Validators.required,
          this.commissioningDateValidator(this.isAlreadyInOperation, this.marketingType),
        ],
      }),
      applicableValueCentsPerkWh: new FormControl<number | null>(null, {
        validators: applicableValueValidators,
        updateOn: 'blur',
      }),
      latitude: new FormControl<number | null>(null, { updateOn: 'blur' }),
      longitude: new FormControl<number | null>(null, { updateOn: 'blur' }),
    })

    const mastrIdGenerationUnitControl = commonResource.controls.mastrIdGenerationUnit
    mastrIdGenerationUnitControl.valueChanges.subscribe((value) => {
      if (value) {
        mastrIdGenerationUnitControl.setValue(value.toUpperCase(), { emitEvent: false })
      }
    })

    return commonResource
  }

  private buildSolarResource(): FormGroup {
    const solarFormGroup = this.buildCommonResource()
    solarFormGroup.addControl('solarTrackedModules', new FormControl<boolean | null>(null, [Validators.required]))
    solarFormGroup.addControl('solarBifacial', new FormControl<boolean | null>(null, [Validators.required]))
    solarFormGroup.addControl('solarTiltDegree', new FormControl<number | null>(null))
    solarFormGroup.addControl('solarOrientation', new FormControl<string | null>(null))
    return solarFormGroup
  }

  private buildWindResource(): FormGroup {
    const windFormGroup = this.buildCommonResource()
    windFormGroup.addControl('turbineManufacturer', new FormControl<string | null>(null, [Validators.required]))
    windFormGroup.addControl('turbineType', new FormControl<string | null>(null, [Validators.required]))
    windFormGroup.addControl(
      'turbineHubHeight',
      new FormControl<number | null>(null, { validators: this.validatorForTurbineHubHeight(), updateOn: 'blur' })
    )
    return windFormGroup
  }

  deleteResource(event: MouseEvent, index: number) {
    event.stopPropagation()
    if (this.formArray.length < 2) {
      return
    }
    this.dialog
      .open(ResourceDeletionConfirmationComponent, {
        data: this.getTechnicalResourceTitle(index),
      })
      .afterClosed()
      .subscribe((confirmed) => {
        if (confirmed) {
          console.log(`Resource ${index + 1} confirmed to be deleted`)
          this.formArray.removeAt(index)
        }
      })
  }

  getControl(resource: FormGroup, controlName: string) {
    return resource.get(controlName) as FormControl
  }

  isExpanded(resourceIndex: number) {
    return resourceIndex == this.formArray.length - 1
  }

  getResourceStatus(resource: FormGroup) {
    return resource.valid ? 'Vollständig' : 'Unvollständig'
  }

  getStatusLabelStyle(formGroup: FormGroup) {
    return formGroup.valid ? 'default' : 'secondary'
  }

  getTechnicalResourceTitle(index: number): string {
    return `TR-${index + 1}`
  }

  get applicableValueTooltip(): string {
    const prodTech = this.formGroup.controls.productionTechnology.value
    const inOperation = this.formGroup.controls.isAlreadyInOperation.value

    if (prodTech === ProductionTechnology.Solar && inOperation) {
      return 'Der anzulegende Wert (ehemals auch EEG-Einspeisevergütung), ist ein fester Betrag, den Sie für die Einspeisung von Strom aus erneuerbaren Energien 20 Jahre lang erhalten. Diese Vergütung ist entscheidend für die Wirtschaftlichkeit Ihrer Anlage und wird von der Bundesnetzagentur festgelegt. Entnehmen Sie Ihren anzulegenden Wert unserem EEG Rechner.'
    } else if (
      prodTech === ProductionTechnology.WindOnshore ||
      (prodTech === ProductionTechnology.Solar && !inOperation)
    ) {
      return 'Der anzulegende Wert (ehemals auch EEG-Einspeisevergütung), ist ein fester Betrag, den Sie für die Einspeisung von Strom aus erneuerbaren Energien 20 Jahre lang erhalten. Diese Vergütung ist entscheidend für die Wirtschaftlichkeit Ihrer Anlage und wird von der Bundesnetzagentur festgelegt.'
    }
    return ''
  }

  getWindResourceControl(formGroup: FormGroup): FormGroup<WindResourceFormControls> {
    return formGroup as FormGroup<WindResourceFormControls>
  }

  getSolarResourceControl(resource: FormGroup): FormGroup<SolarResourceFormControls> {
    return resource as FormGroup<SolarResourceFormControls>
  }

  getGenericResourceControl(resource: FormGroup): FormGroup<GenericResourceFormControls> {
    return resource as FormGroup<GenericResourceFormControls>
  }

  commissioningDateValidator(
    isAlreadyInOperationSignal: Signal<boolean | null>,
    marketingType: Signal<MarketingType | null>
  ): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const value = control.value
      if (!value) {
        return null
      }

      const today = moment().startOf('day')
      const selectedDate = moment(value).startOf('day')
      if (isAlreadyInOperationSignal()) {
        const minDate = moment(this.COMMISSIONING_DATE_MIN).startOf('day')
        return selectedDate.isAfter(today)
          ? { dateInFutureNotAllowed: true }
          : selectedDate.isBefore(minDate) && marketingType() == MarketingType.Other
            ? { dateBeforeMinDateNotAllowed: true }
            : null
      } else {
        if (selectedDate.isBefore(today)) {
          return { dateInPastNotAllowed: true }
        } else if (!this.fromNowTo184Filter(selectedDate)) {
          return { dateLaterThanSixMonthNotAllowed: true }
        }
        return null
      }
    }
  }

  private setRequiredValidatorForApplicableValueCentsPerkWh(
    resource: FormGroup<WindResourceFormControls | SolarResourceFormControls>,
    isRequired: boolean
  ): void {
    const control = resource.controls.applicableValueCentsPerkWh
    if (isRequired) {
      control.addValidators(Validators.required)
    } else {
      control.clearValidators()
      control.setValidators(this.validatorForApplicableValueCentsPerkWh())
    }
    control.updateValueAndValidity()
  }

  validatorForTurbineHubHeight(): ValidatorFn[] {
    return [
      Validators.required,
      Validators.min(10),
      Validators.max(250),
      Validators.pattern(DIGIT_WITH_UP_TO_THREE_DECIMALS),
      CustomValidators.zeroIsNotAllowedAsFirstCharacter(),
    ]
  }

  validatorForApplicableValueCentsPerkWh(): ValidatorFn[] {
    return [
      Validators.min(1),
      Validators.max(99.99),
      Validators.pattern(DIGIT_WITH_UP_TO_TWO_DECIMALS),
      CustomValidators.zeroIsNotAllowedAsFirstCharacter(),
    ]
  }

  isApplyMarketRegisterDataButtonDisabled(
    resource: FormGroup<WindResourceFormControls | SolarResourceFormControls>
  ): boolean {
    const unitControl = resource.controls.mastrIdGenerationUnit
    return !unitControl.valid || !unitControl.value
  }

  applyMarketRegisterData(
    resource: FormGroup<SolarResourceFormControls | WindResourceFormControls>,
    resourceIndex: number
  ) {
    this.marketDataService
      .getGenerationUnitData(this.productionTechnology()!, resource.controls.mastrIdGenerationUnit.value!)
      .subscribe({
        next: (marketRegisterData: MarketRegisterSolarResource | MarketRegisterWindResource) => {
          this.patchResource(marketRegisterData, resource)
          this.validateMarketRegisterCommissioningDate(marketRegisterData)
          this.onMarketRegisterDataFetchSucceed(resourceIndex)
        },
        error: (error: { status: number }) => {
          console.log(error)
          this.onMarketRegisterDataFetchFailed(error.status, resourceIndex)
        },
      })
  }

  private onMarketRegisterDataFetchFailed(statusCode: number, resourceIndex: number) {
    if (statusCode === HttpStatusCode.NotFound) {
      const resourceTitle = this.getTechnicalResourceTitle(resourceIndex)
      this.notificationService.error(
        `Datenabruf für Stromerzeugungseinheit [${resourceTitle}] fehlgeschlagen`,
        `Überprüfen Sie die Marktstammdatenregister-Nummer der Einheit [${resourceTitle}]. Stellen Sie sicher, dass die im Marktstammdatenregister angegebene Erzeugungstechnologie der oben angegebenen entspricht. Falls die Stromerzeugungseinheit noch nicht im Marktstammdatenregister registriert ist, können Sie dies unter www.marktstammdatenregister.de tun. Rufen Sie die Daten danach erneut ab.`
      )
    } else {
      this.notificationService.error(
        'Verbindung zum Marktstammdatenregister fehlgeschlagen',
        'Wir können aktuell keine Verbindung zum Marktstammdatenregister herstellen. Bitte tragen Sie die Daten manuell ein oder versuchen Sie es später erneut.'
      )
    }
    this.trackEventService.trackEvent('master_data_fetch_error', 'Button', 'error', 'Fetch Data')
  }

  private onMarketRegisterDataFetchSucceed(index: number) {
    this.notificationService.confirm(
      `Datenabruf für Stromerzeugungseinheit [${this.getTechnicalResourceTitle(index)}] erfolgreich`,
      'Die Daten konnten erfolgreich abgerufen werden. Bitte ergänzen Sie die noch fehlenden Daten zur Stromerzeugungseinheit.'
    )
    this.trackEventService.trackEvent('master_data_fetch_success', 'Button', 'success', 'Fetch Data')
  }

  private validateMarketRegisterCommissioningDate(marketRegisterData: MarketRegisterResource) {
    const isSingleResource = this.resourceCounter() == 1
    if (this.isAlreadyInOperation() && marketRegisterData.commissioningDatePlanned) {
      this.offerFormService.showCommissioningDateGlobalError(true, isSingleResource)
    } else if (!this.isAlreadyInOperation() && marketRegisterData.commissioningDate) {
      this.offerFormService.showCommissioningDateGlobalError(false, isSingleResource)
    }
  }

  private patchResource(
    marketRegisterData: MarketRegisterSolarResource | MarketRegisterWindResource,
    resource: FormGroup<SolarResourceFormControls | WindResourceFormControls>
  ) {
    resource.patchValue(marketRegisterData)
  }

  getLocationValiditySignal(resourceId: number): WritableSignal<boolean> {
    if (!this.locationValidMap.get(resourceId)) {
      this.locationValidMap.set(resourceId, signal(true))
    }
    return this.locationValidMap.get(resourceId)!
  }

  trackEventApplyMarketRegisterData() {
    this.trackEventService.trackEvent('master_data_fetch', 'Button', 'click', 'Fetch Data')
  }

  protected readonly ViewFormatterService = ViewFormatterService
}
